/**
 * lib/pettyCash.ts
 * ------------------------------------------------------------------
 * PETTY CASH ON THE IMPREST SYSTEM.
 *
 * A school spends real money in small amounts — a taxi for a sick child, a
 * plumber's part, printing for an event — and none of it fits the voucher
 * chain, which is correct: three signatures for P80 is a control nobody follows.
 * So it happens in cash, out of a tin, and unless something counts it, it is the
 * one part of the accounts with no evidence at all behind it.
 *
 * THE IMPREST IDENTITY is the whole control, and it is worth stating plainly
 * because everything below exists to make it checkable:
 *
 *     cash in the tin  +  slips not yet reimbursed  =  the float
 *
 * The float is a FIXED amount. Money is not added to it ad hoc; it is restored
 * to its fixed value by reimbursing exactly what was spent. That is what makes a
 * shortage impossible to hide: any other arrangement lets a top-up quietly cover
 * a missing note, whereas here the identity simply stops holding.
 *
 * WHEN THE EXPENSE IS RECOGNISED. Strict imprest recognises expenses only at
 * reimbursement, which leaves the petty-cash account permanently showing the
 * float while the tin holds something else. We recognise at the SLIP instead —
 * Dr expense, Cr PETTY_CASH — so the ledger balance is what should physically be
 * in the tin, and a cash count can be checked against the ledger directly rather
 * than against an arithmetic exercise. Reimbursement then only moves money:
 * Dr PETTY_CASH, Cr bank.
 *
 * PURE. Types, the identity, and the reconciliation. No Firestore, no React.
 * ------------------------------------------------------------------ */

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export const PETTY_COLLECTIONS = {
  floats: 'financePettyFloats',
  slips: 'financePettySlips',
  counts: 'financePettyCounts',
  counters: 'financeCounters',
} as const;

export const pettySlipCounterId = (year: string): string => `pettySlips-${year}`;
export const formatPettySlipNo = (seq: number, year: string): string =>
  `PC/${year}/${String(seq).padStart(4, '0')}`;

/* =============================== types =============================== */

/**
 * One tin, with one person responsible for it.
 *
 * A float without a named custodian is a float nobody is accountable for, which
 * is how "the office keeps some cash" becomes an unauditable balance. The
 * custodian is required, and changing them is a deliberate act that should be
 * preceded by a count.
 */
export interface PettyFloat {
  id: string;
  name: string;
  /** The fixed imprest amount. */
  floatAmount: number;
  custodianId: string;
  custodianName: string;
  /** GL account holding the cash — normally CASH_ON_HAND. */
  accountCode: string;
  active: boolean;
  openedAt: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
}

export interface PettySlip {
  id: string;
  slipNo: string;
  floatId: string;
  date: string;
  /** What it was for, in the words of whoever spent it. */
  description: string;
  amount: number;
  /** The expense account. Required — an uncoded slip is a hole in the accounts
   *  that someone has to guess at later. */
  accountCode: string;
  /** Who the money was given to or spent with. */
  paidTo: string;
  /** Whether a till slip or receipt was handed in. Recorded rather than
   *  enforced: refusing a slip with no receipt does not make the money come
   *  back, and it does guarantee the spend goes unrecorded entirely. */
  hasReceipt: boolean;
  fy: string;
  /** Set when this slip has been reimbursed, naming the claim. */
  reimbursementId?: string;
  recordedBy: string;
  recordedByName?: string;
  createdAt: string;
  /** Set when reversed; corrections are new slips, like everything else. */
  reversedBy?: string;
  reversesId?: string;
}

/**
 * A physical count of the tin.
 *
 * The variance is stored, never silently corrected. A recurring small shortage
 * is a pattern; a corrected one is invisible.
 */
export interface PettyCount {
  id: string;
  floatId: string;
  date: string;
  countedAmount: number;
  /** Ledger balance at the moment of counting. */
  expectedAmount: number;
  /** counted − expected. Negative is a shortage. */
  variance: number;
  explanation?: string;
  countedBy: string;
  countedByName?: string;
  /** The journal that wrote the variance off, when one was posted. */
  journalId?: string;
  createdAt: string;
}

/* ========================== the imprest state ========================== */

export interface FloatState {
  floatAmount: number;
  /** Slips recorded since the last reimbursement — what can be claimed back. */
  unreimbursedTotal: number;
  unreimbursedCount: number;
  /** What should physically be in the tin: float less unreimbursed spending. */
  cashOnHand: number;
  /** How much to draw to restore the float. Equals unreimbursedTotal — stated
   *  separately because that equality IS the imprest system, and a claim that
   *  differs from it is the error worth catching. */
  reimbursementDue: number;
  /** Proportion of the float already spent. Drives "time to top up". */
  spentPct: number;
  lowOnCash: boolean;
  /** Net of every count variance written off against this float. Negative where
   *  the tin has been found short. Part of `cashOnHand`, and reported on its own
   *  so a recurring shortage is visible rather than absorbed. */
  countVariance: number;
}

/**
 * Where a float stands right now.
 *
 * Computed from the slips and the counts, never stored — a stored "cash
 * remaining" is a figure that drifts from its own history the first time a slip
 * is reversed.
 *
 * COUNT VARIANCES ARE PART OF THE ANSWER, and leaving them out was a real error.
 * When a count finds the tin P20 short, that shortage is written off to expenses
 * and the cash genuinely IS P20 lower. Computing the expected cash from the
 * float and the slips alone keeps reporting the pre-count figure, so the tin
 * reads fuller than it is and the petty-cash control breaks by every shortage
 * ever recorded — permanently, since nothing else ever reconciles it away.
 *
 * A shortage carries a negative variance, so adding it reduces the cash.
 */
export function floatState(
  float: Pick<PettyFloat, 'floatAmount'>,
  slips: PettySlip[],
  counts: Pick<PettyCount, 'variance'>[] = [],
  lowWaterPct = 75,
): FloatState {
  const live = slips.filter((s) => !s.reimbursementId && !s.reversedBy && !s.reversesId);
  const unreimbursedTotal = round2(live.reduce((n, s) => n + (Number(s.amount) || 0), 0));
  const floatAmount = round2(float.floatAmount);
  const countVariance = round2(counts.reduce((n, c) => n + (Number(c.variance) || 0), 0));
  const cashOnHand = round2(floatAmount - unreimbursedTotal + countVariance);
  return {
    countVariance,
    floatAmount,
    unreimbursedTotal,
    unreimbursedCount: live.length,
    cashOnHand,
    reimbursementDue: unreimbursedTotal,
    spentPct: floatAmount > 0 ? Math.round((unreimbursedTotal / floatAmount) * 100) : 0,
    lowOnCash: floatAmount > 0 && (unreimbursedTotal / floatAmount) * 100 >= lowWaterPct,
  };
}

/**
 * Can this slip be paid out of the tin?
 *
 * Refusing an over-spend matters more here than elsewhere: the tin cannot go
 * negative in reality, so a slip that takes it below zero means either the
 * amount is wrong or cash came from somewhere unrecorded. Both need answering
 * before the entry is made, not after.
 */
export function canSpend(state: FloatState, amount: number): { ok: boolean; reason?: string } {
  const a = round2(amount);
  if (!(a > 0)) return { ok: false, reason: 'Enter an amount greater than nil.' };
  if (a > state.cashOnHand + 0.005) {
    return {
      ok: false,
      reason: `Only ${state.cashOnHand.toFixed(2)} should be left in the tin. `
        + 'Either the amount is wrong, or cash has come from somewhere that is not recorded.',
    };
  }
  return { ok: true };
}

export interface CountResult {
  countedAmount: number;
  expectedAmount: number;
  variance: number;
  /** Nothing to explain. */
  balanced: boolean;
  short: boolean;
  over: boolean;
  /** Plain words for the person holding the tin. */
  summary: string;
}

/**
 * Reconcile a physical count against what the float state says should be there.
 *
 * An OVER is reported as loudly as a SHORT. A tin with more money in it than the
 * records show is not good news — it means a slip was never written, or cash
 * that belongs somewhere else has been put in, and both are the same kind of
 * failure as money going missing.
 */
export function reconcileCount(state: FloatState, countedAmount: number): CountResult {
  const counted = round2(countedAmount);
  const expected = state.cashOnHand;
  const variance = round2(counted - expected);
  const balanced = Math.abs(variance) < 0.005;
  return {
    countedAmount: counted,
    expectedAmount: expected,
    variance,
    balanced,
    short: variance < -0.005,
    over: variance > 0.005,
    summary: balanced
      ? 'The tin agrees with the records.'
      : variance < 0
        ? `${Math.abs(variance).toFixed(2)} short. Something was spent without a slip, or cash is missing.`
        : `${variance.toFixed(2)} over. A slip was written for money that was not spent, or cash from elsewhere is in the tin.`,
  };
}

/**
 * The imprest identity itself, for a screen that wants to show the working.
 *
 * Cash + unreimbursed slips = the float, ONCE ANY WRITTEN-OFF SHORTAGE IS ADDED
 * BACK. A tin that was found P20 short and had that shortage written off will
 * never again add up to the float on cash and slips alone — the P20 left the
 * system as an expense, and pretending otherwise would make the identity look
 * broken for the rest of the float's life.
 */
export function imprestCheck(state: FloatState, countedAmount: number): {
  holds: boolean; left: number; right: number;
} {
  const left = round2(round2(countedAmount) + state.unreimbursedTotal - state.countVariance);
  return { holds: Math.abs(round2(left - state.floatAmount)) < 0.005, left, right: state.floatAmount };
}
